// Festival dates for the greeting sheet (D176), by region (D266). Festival
// days move every year with the lunar calendar, so they're looked up, not
// computed: one row per festival per year per region. Dates are local
// calendar days (YYYY-MM-DD) where the region's reference city is; the sheet
// compares them with the device's own date. Where a festival lasts several
// days, `first` and `last` are its first and last days.
//
// The table itself is shared/contracts/festival-dates.json, so the native
// apps can bundle the same data. Which region a device uses comes from its
// time zone (festivalRegion.ts); nothing is sent to the server.
//
// When the table runs out, festival cards stop appearing (no guessing):
// extend it before FESTIVAL_TABLE_LAST_YEAR ends. A test in
// greetings.test.ts starts failing in October of that year as a reminder.
//
// Sources: Drik Panchang's festival pages, each set to the region's city
// with `geoname-id` (the page's city selector), for year=2026, 2027, 2028.
// Every page below was opened 2026-10-26, for every city. New Delhi's dates
// match the earlier table (Drik Panchang's Indian calendar, opened
// 2026-10-24).
//   City (region)            geoname-id
//   New Delhi (india)        1261481
//   Toronto (canada-east)    6167865
//   Vancouver (canada-west)  6173331
//   London (uk)              2643743
//   Dubai (gulf)             292223
//   Singapore (singapore)    1880252
//   Sydney (australia)       2147714
// Pages, on www.drikpanchang.com, each with ?geoname-id=<id>&year=YYYY:
// - Lohri         /calendars/indian/sikh-festivals/lohri/lohri-date-time.html
// - Thai Pongal   /festivals/pongal/thai-pongal-date-time.html
// - Rangwali Holi /festivals/holi/festivals-holi-rangwali.html
// - Eid al-Fitr   /calendars/indian/muslim-festivals/eid-al-fitr/eid-al-fitr-date-time.html
// - Bakrid        /calendars/indian/muslim-festivals/bakrid/eid-al-adha-date-time.html
// - Raksha Bandhan /festivals/raksha-bandhan/raksha-bandhan-date-time.html
// - Onam          /festivals/onam/onam-calendar.html (Atham to Thiruvonam)
// - Navratri      /navratri/ashwin-shardiya-navratri-dates.html (Ghatasthapana to Maha Navami)
// - Durga Puja    /navratri/durga-puja/durga-puja-calendar.html (Shashthi to Dashami)
// - Dhanteras     /festivals/dhanteras/festivals-dhanteras-puja-timings.html (Diwali's first day)
// - Lakshmi Puja  /festivals/lakshmipuja/festivals-lakshmipuja-timings.html
// - Bhai Dooj     /festivals/bhai-dooj/bhai-dooj-date-time.html (Diwali's last day)
// Not read (2026-10-26): Drik Panchang answered with a captcha for every
// Toronto page, and for Dubai's Lohri, Pongal, Eid al-Fitr, Navratri,
// Durga Puja and Bhai Dooj pages and Bakrid 2028. Those rows are left out
// rather than guessed, so the region borrows its `fallback` region's:
// canada-east uses Vancouver's (the nearest region), and gulf uses New
// Delhi's for those festivals (Dubai's own Holi, Raksha Bandhan and Onam
// are in). Toronto's Hindu calendar for 2026
// (/calendars/hindu/hinducalendar.html?geoname-id=6167865&year=2026,
// opened 2026-10-26) agrees with Vancouver's 2026 rows where they overlap:
// Holi 3 March, Raksha Bandhan 27 August, Bhai Dooj 10 November.
// Eid al-Fitr and Bakrid are Drik Panchang's dates for each city; local
// moon sighting can move them a day, which the sheet's window allows for
// (a day wider each way, D176).
// Christmas (25 December) and New Year (1 January) are fixed and need no table.
//
// The other festivals (added 2026-10-29). Every page below was opened
// 2026-10-29 unless it says otherwise; "seen in search results" means
// Drik Panchang answered 403 and the date was read from the search
// engine's copy of the page.
// Hindu festivals, every region but canada-east (which borrows Vancouver's):
// Drik Panchang's Hindu calendar, /calendars/hindu/hinducalendar.html
// ?geoname-id=<id>&year=2026, 2027 and 2028, for Makar Sankranti, Vasant
// Panchami, Ratha Saptami, Maha Shivratri, Holika Dahan, Gudi Padwa (Ugadi),
// Chaitra Navratri (Ghatasthapana to Rama Navami; checked on
// /navratri/chaitra-vasant-navratri-dates.html for New Delhi 2026), Gangaur,
// Ram Navami, Hanuman Jayanti, Akshaya Tritiya, Parshuram Jayanti, Buddha
// Purnima, Vat Savitri, Ganga Dussehra, Rath Yatra, Guru Purnima, Hariyali
// Teej, Nag Panchami, Kajari Teej, Janmashtami, Ganesh Chaturthi, Hartalika
// Teej, Dussehra, Sharad Purnima, Karva Chauth (2027–2028 and the regions),
// Ahoi Ashtami, Chhath Puja, Tulsi Vivah, Vivah Panchami and Gita Jayanti.
// Where Smarta and ISKCON days differ (Ram Navami, Janmashtami), `first` is
// the Smarta day and `last` the ISKCON one. Chhath Puja is Nahay Khay to
// Usha Arghya, laid out around Sandhya Arghya as on
// /chhath/chhath-puja-calendar.html (New Delhi 2026; the other years and
// cities by the same layout, the page then answered 403).
// Regional days, each page with ?geoname-id=<id>&year=YYYY for New Delhi,
// Vancouver and London (Singapore's 2026 pages agreed with New Delhi's,
// so Singapore borrows India's rows rather than ending after 2026):
// - Bhogi             /festivals/pongal/bhogi-pandigai-date-time.html
// - Uttarayan         /festivals/sankranti/uttarayana/uttarayana-date-time.html
// - Magh Bihu         /festivals/sankranti/magh-bihu/magh-bihu-date-time.html
// - Vishu             /festivals/vishu/vishu-kani-date-time.html
// - Puthandu          /festivals/puthandu/tamil-newyear-date-time.html
// - Pohela Boishakh   /festivals/pohela-boishakh/pohela-boishakh-date-time.html
// - Karthigai Deepam  /festivals/karthigai-deepam/karthigai-deepam-date-time.html
// - Kali Puja         /diwali/kali-puja/bengal-kalipuja-date-time.html
// - Thaipusam         /festivals/thaipusam/tamil-thaipusam-date.html
// - Cheti Chand       /hindu-saints/jhulelal/cheti-chand-date-time.html
// - Dev Deepawali     /diwali/dev-diwali/dev-deepawali-date-time.html (New
//   Delhi only; the page always shows Varanasi)
// India only, the other regions borrowing New Delhi's:
// - Baisakhi: officeholidays.com/holidays/india/jammu-and-kashmir/vaisakhi
//   (2026, 2027), publicholidays.in/vaisakh (2026–2028), the Government of
//   India's 2027 restricted holidays (DoP&T O.M. of 16 July 2026, Annexure
//   II, as printed on staffnews.in: Vaisakhi 14 April) and Drik Panchang's
//   /festivals/month/festivals-april.html?year=2028 (seen in search
//   results: 13 April 2028, Mesha Sankranti).
// - Bihu (Rongali / Bohag): Assam's holidays, publicholidays.in/bohag-bihu
//   (14–15 April 2026 and 2027; 14 April 2028, an estimate until Assam
//   publishes 2028's list), with DoP&T's restricted holidays for 2026 and
//   2027 (Bahag Bihu 15 April).
// - Guru Nanak Jayanti: DoP&T's 2027 gazetted holidays (Annexure I, as
//   printed on staffnews.in: 14 November 2027), publicholidays.in/guru-nanak-jayanti
//   (24 November 2026, 14 November 2027, 2 November 2028),
//   dekhopanchang.com/en/festivals/guru-nanak-jayanti/2028 and Drik
//   Panchang's /calendars/indian/sikh-festivals/jayanti/guru-nanak-jayanti-date.html?year=2026
//   (seen in search results: 24 November 2026, Kartik Purnima).
// - Mahavir Jayanti: DoP&T's 2027 gazetted holidays (19 April 2027) and
//   publicholidays.in/mahavir-jayanti (31 March 2026, 19 April 2027, 7 April
//   2028); officeholidays.com says 18 April 2027, where Drik Panchang's
//   default (US) city has it, but in India Trayodashi holds at sunrise on
//   the 19th.
// - Guru Gobind Singh Jayanti: /calendars/indian/sikh-festivals/jayanti/guru-gobind-jayanti-date.html
//   ?geoname-id=1261481 (the panchang's Paush Shukla Saptami; none falls
//   in 2026, and 2028 has two, 4 January and 23 December: the table holds
//   the first).
// - Hola Mohalla: orsl.usc.edu/life/sikh-holy-days-calendar (2027, 2028);
//   2026 from Outlook India and the Tanenbaum fact sheet (seen in search
//   results). Provisional until the SGPC announces each year.
// - Paryushan, Samvatsari: /festivals/month/festivals-august.html and
//   festivals-september.html?geoname-id=1261481 ("Paryushana Parvarambha"
//   to "Samvatsari Parva"). Das Lakshan: Rishi Panchami to Anant
//   Chaturdashi on the same pages.
// - Losar: officeholidays.com/holidays/india/sikkim/losar and
//   en.wikipedia.org/wiki/Losar (2026, 2027; no source has 2028 yet).
// - Islamic New Year, Milad-un-Nabi: /calendars/indian/muslim-festivals/
//   al-hijra/islamic-newyear-date-time.html and milad-un-nabi/milad-un-nabi-date-time.html
//   ?geoname-id=1261481 (DoP&T's 2027 list has Milad-un-Nabi on 15
//   August, a day before Drik Panchang's; the moon slack covers it).
// - Ramadan begins: 2026 as announced (Lucknow Markazi Chand Committee,
//   reported by Free Press Journal and others); 2027 timeanddate.com's
//   tentative date for India (seen in search results). 2028 not yet.
// - Shab-e-Barat: 2026 Indian news (night of 3 February) and
//   officeholidays.com/holidays/india/shab-e-barat (Bihar, 24 January 2027,
//   so the night of the 23rd). 2028 not yet.
// - Shab-e-Qadr: the 27th night of the observed Ramadan 2026 (16 March).
//   2027 and 2028 wait for their Ramadan.
// - Parsi New Year (Shahenshahi Navroz): officeholidays.com/holidays/india/parsi-new-year
//   (15 August 2026 and 2027; 2028 a day earlier, the Shahenshahi year
//   having no leap day; DoP&T's 2027 list agrees on 15 August). Khordad
//   Sal: Navroz + 5 days. Jamshedi Navroz (Fasli): the March equinox day
//   by the Iranian noon rule (en.wikipedia.org/wiki/Nowruz).
// - Republic Day, Independence Day, Gandhi Jayanti, Teachers' Day,
//   Children's Day: fixed dates. Friendship Day: 1st Sunday of August.
// Every region: Easter by the Gregorian computus (5 April 2026, 28 March
// 2027, 16 April 2028; DoP&T's 2027 lists agree: Good Friday 26 March,
// Easter Sunday 28 March), Good Friday two days before and Palm Sunday a
// week before; International Women's Day, 8 March; Mother's Day, the 2nd
// Sunday of May (the UK's Mothering Sunday three weeks before Easter; the
// Gulf borrows India's); Father's Day, the 3rd Sunday of June (Australia's
// the 1st Sunday of September).
// One region only, so nowhere else shows them: Lunar New Year in Singapore
// (www.mom.gov.sg/employment-practices/public-holidays for 2026–2027; 2028
// from en.wikipedia.org/wiki/Chinese_New_Year), Thanksgiving (2nd Monday of
// October) and Grandparents' Day (the Sunday after Labour Day) in Canada.
// Left out: Bathukamma and Bonalu (no source agrees on 2027 or has 2028),
// Mahalaya (the last day of Pitru Paksha), and mourning days.

import table from '../../../shared/contracts/festival-dates.json';
import { festivalRegion, type FestivalRegion } from './festivalRegion';

export type { FestivalRegion } from './festivalRegion';

export type FestivalId =
  | 'diwali'
  | 'holi'
  | 'raksha-bandhan'
  | 'eid'
  | 'eid-al-adha'
  | 'christmas'
  | 'pongal'
  | 'onam'
  | 'navratri'
  | 'lohri'
  | 'durga-puja'
  | 'karva-chauth'
  | 'new-year'
  | 'bhogi'
  | 'makar-sankranti'
  | 'uttarayan'
  | 'magh-bihu'
  | 'guru-gobind-singh-jayanti'
  | 'vasant-panchami'
  | 'ratha-saptami'
  | 'thaipusam'
  | 'republic-day'
  | 'lunar-new-year'
  | 'losar'
  | 'shab-e-barat'
  | 'ramadan-start'
  | 'maha-shivratri'
  | 'holika-dahan'
  | 'hola-mohalla'
  | 'womens-day'
  | 'shab-e-qadr'
  | 'gudi-padwa'
  | 'cheti-chand'
  | 'chaitra-navratri'
  | 'jamshedi-navroz'
  | 'gangaur'
  | 'ram-navami'
  | 'mahavir-jayanti'
  | 'hanuman-jayanti'
  | 'palm-sunday'
  | 'good-friday'
  | 'easter'
  | 'baisakhi'
  | 'bihu'
  | 'vishu'
  | 'puthandu'
  | 'pohela-boishakh'
  | 'akshaya-tritiya'
  | 'parshuram-jayanti'
  | 'buddha-purnima'
  | 'mothers-day'
  | 'vat-savitri'
  | 'ganga-dussehra'
  | 'islamic-new-year'
  | 'fathers-day'
  | 'rath-yatra'
  | 'guru-purnima'
  | 'hariyali-teej'
  | 'nag-panchami'
  | 'friendship-day'
  | 'independence-day'
  | 'parsi-new-year'
  | 'khordad-sal'
  | 'kajari-teej'
  | 'milad-un-nabi'
  | 'janmashtami'
  | 'paryushan'
  | 'samvatsari'
  | 'das-lakshan'
  | 'teachers-day'
  | 'grandparents-day'
  | 'ganesh-chaturthi'
  | 'hartalika-teej'
  | 'gandhi-jayanti'
  | 'dussehra'
  | 'thanksgiving'
  | 'sharad-purnima'
  | 'ahoi-ashtami'
  | 'kali-puja'
  | 'chhath-puja'
  | 'childrens-day'
  | 'guru-nanak-jayanti'
  | 'tulsi-vivah'
  | 'dev-deepawali'
  | 'karthigai-deepam'
  | 'vivah-panchami'
  | 'gita-jayanti';

export interface FestivalDays {
  /** The festival's first day, YYYY-MM-DD. */
  first: string;
  /** Its last day; the same as `first` for a one-day festival. */
  last: string;
}

export type TabledFestival = Exclude<FestivalId, 'christmas' | 'new-year'>;

type RegionTable = Record<TabledFestival, Record<string, FestivalDays>>;

interface FestivalTableFile {
  firstYear: number;
  lastYear: number;
  regions: Record<
    FestivalRegion,
    {
      city: string;
      geonameId: number;
      /** A region whose city page couldn't be read borrows this one's dates. */
      fallback?: FestivalRegion;
      festivals?: Partial<RegionTable>;
    }
  >;
}

const FILE = table as FestivalTableFile;

/** The last year the table covers. */
export const FESTIVAL_TABLE_LAST_YEAR = FILE.lastYear;
export const FESTIVAL_TABLE_FIRST_YEAR = FILE.firstYear;

/** Every region's table as written in the file (a region may have none). */
export const FESTIVAL_DATES_BY_REGION: Record<
  FestivalRegion,
  Partial<RegionTable>
> = Object.fromEntries(
  Object.entries(FILE.regions).map(([region, entry]) => [
    region,
    entry.festivals ?? {},
  ])
) as Record<FestivalRegion, Partial<RegionTable>>;

/** India's table (New Delhi), the default. */
export const FESTIVAL_DATES = FESTIVAL_DATES_BY_REGION.india as RegionTable;

const day = (date: string): FestivalDays => ({ first: date, last: date });

/**
 * The festival's days in `year` as observed in `region`, or null where the
 * table has none. A region without its own row uses its `fallback`
 * region's, then India's. This is the API festival mode (#49) uses.
 */
export function festivalDaysFor(
  region: FestivalRegion,
  id: FestivalId,
  year: number
): FestivalDays | null {
  if (id === 'christmas') return day(`${year}-12-25`);
  if (id === 'new-year') return day(`${year}-01-01`);
  const seen = new Set<FestivalRegion>();
  let current: FestivalRegion | undefined = region;
  while (current && !seen.has(current)) {
    seen.add(current);
    const entry: FestivalTableFile['regions'][FestivalRegion] | undefined =
      FILE.regions[current];
    const row = entry?.festivals?.[id]?.[String(year)];
    if (row) return row;
    // Only a region with no row at all borrows; a table that simply ends
    // stays ended (no guessing).
    if (entry?.festivals?.[id]) return null;
    current = entry?.fallback ?? (current === 'india' ? undefined : 'india');
  }
  return null;
}

/** The festival's days in `year` for this device's region (D266). */
export function festivalDays(
  id: FestivalId,
  year: number,
  region: FestivalRegion = festivalRegion()
): FestivalDays | null {
  return festivalDaysFor(region, id, year);
}
